import { useEffect, useMemo, useRef, useState } from 'react';
import { Logger, StorageService, type AppState, type AppDispatch } from '@nextgen-web-framework/core';
import {
  useContentSlot,
  AccountType,
  useSession,
  useNextgenRouter,
  useTranslation,
  sendUtagView,
  useTealiumDataLayer,
} from '@nextgen-web-framework/all';
import { useSelector, useDispatch } from 'react-redux';
import {
  updateRegistrationData,
  profileApi,
  RegFlowConfig,
  updateCustomerType,
  RegistrationFlowDataType,
  ProfileConstantsEnum,
  useRegistrationStartEvent,
  getProfileImagesCmsDataTransformer,
  updateRegistrationCmsData,
  getAccountTypeImagesCmsDataTransformer,
  getTermsConditionCmsDataTransformer,
  updateAccountTypeImagesCMS,
  updateTermsNConditionsCMS,
  updateRegistrationOrderConfirmationImagesCMSData,
  updateCurrentFlowStepsData,
  resetRegistrationState,
  RegistrationStatus,
  useRegistrationCompletionEvent,
  updateApiErrorData,
  type AccountTypeImagesResult,
  type DynamicObjectInterface,
  type RegistrationCreateRequest,
  type RegistrationNextHandlerPatchPayload,
  type RegistrationResponse,
  type RegistrationSubmitRequest,
  type RegistrationSubmitResponse,
  type InitialProfileReduxUpdate,
  type TermsConditionResult,
  type onSuccessPropType,
} from '@commerce-profile-webcomponents/sdk';

import { PageLoader } from '@commerce-webcomponents/ui';
import RegistrationSignInModal from '../RegistrationSignInModal/RegistrationSignInModal';
import RegistrationFallback from '../RegistrationFallback/RegistrationFallback';
import {
  getLoginRedirectProps,
  getRegistrationFlow,
  loginAndRedirectAfterRegistration,
  getPersonalDetailData,
  updateSponsor,
  generateCustomHeaders,
  updateSponsorAttribute,
  restoreRegistrationDataFromSession,
  deleteRegistrationInfoFromSession,
  checkForSkipCart,
} from '../../utils/registrationUtils';
import { useCountryMasterData, useRegistrationFlowConfig } from '../../utils/masterdata';
import { useLocale } from '../../utils/customHooks';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import {
  ERROR_GENERIC_MESSAGE,
  ErrorConstants,
  FLOWS_WITH_POSSIBLE_SPONSOR,
  SHAREBAR_FLOWS,
  SPONSOR_PREDEFINED_FLOW,
} from '../../utils/Constants';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import RegistrationConfirmation from '../RegistrationConfirmation/RegistrationConfirmation';
import RegistrationSelectStarterKit from '../RegistrationSelectStarterKit/RegistrationSelectStarterKit';
import { IProspectInfo, IRegistrationConfirmationPropTypes } from '../RegistrationConfirmation/types/propTypes';
import { ErrorResponseType } from '../../utils/types/propTypes';
import CoAppCommonScreen from '../CoApplicant/CoAppCommonScreen/CoAppCommonScreen';
import { CoAppScreenEnum } from '../CoApplicant/types/propTypes';
import UserOpsCenterDetailsStep from '../RegistrationOpCenterStep/RegistrationOpCenterStep';
import styles from './Registration.module.scss';
import {
  APICallType,
  type RegistrationProps,
  type RegistrationCommonProps,
  type NextHandlerProps,
  type StepType,
} from './types/propTypes';

const Registration = (registrationProps: RegistrationProps) => {
  const {
    findOutMoreUrl,
    sourceUrl,
    redirectUrl,
    flowName,
    onSuccess,
    accountType,
    stepName: paramStepName = '',
    registrationId: paramRegId = '',
    sponsorNumber,
    sponsorName,
    profileInfoCms,
    accountTypeImagesCms = [],
    termsAndConditionsCms = [],
    registrationOrderConfirmImagesCMS = [],
    setSubmitHandler,
    idStepIsLanding,
  } = registrationProps;
  const t = useTranslation();
  const dataLayer = useTealiumDataLayer();
  const registrationFlowData = useSelector(
    (state: AppState) => state.commerceProfile.registrationFlowData,
  ) as RegistrationFlowDataType;

  const {
    isDuplicateUser,
    currentIndex = -1,
    userData,
    perosonalDetails,
    cidVerification,
    selectSponsor,
    documentDataList,
    OtpData,
    coAppPersonalDetails,
  } = registrationFlowData;
  const { sponsor } = selectSponsor;
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);
  const { registrationId = '' } = userData;
  const dispatch = useDispatch<AppDispatch>();
  const router = useNextgenRouter();
  const siteLocale = useLocale();
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const loggerService = Logger.getInstance();

  const { session, isAnonymous } = useSession();
  const { aboNum: accountNum = '' } = session;

  // get master data from e2e
  const config = useRegistrationFlowConfig();
  const SelectAccountTypeStep = useContentSlot('profile', 'SelectAccountTypeStep', RegistrationFallback);
  const UserDetailsStep = useContentSlot('profile', 'UserDetailsStep', RegistrationFallback);
  const ContactVerificationStep = useContentSlot('profile', 'ContactVerificationStep', RegistrationFallback);
  const SelectSponsorStep = useContentSlot('profile', 'selectSponsorStep', RegistrationFallback);
  const SinglePageStep = useContentSlot('profile', 'SinglePageStep', RegistrationFallback);
  const idStep = useContentSlot('profile', 'idStep', RegistrationFallback);
  const UserDetailsStepTh = useContentSlot('profile', 'UserDetailsStepTh', RegistrationFallback);
  const userDetailsStepLatam = useContentSlot('profile', 'userDetailsStepLatam', RegistrationFallback);
  const coAppUserDetailsStep = useContentSlot('profile', 'coAppUserDetailsStep', RegistrationFallback);
  const kitStepLatam = useContentSlot('profile', 'kitStepLatam', RegistrationFallback);

  const coApplicantIdStep = useContentSlot('profile', 'coApplicantIdStep', RegistrationFallback);
  const coApplicantInviteStep = useContentSlot('profile', 'coApplicantInviteStep', RegistrationFallback);
  const coApplicantDocumentUploadStep = useContentSlot(
    'profile',
    'coApplicantDocumentUploadStep',
    RegistrationFallback,
  );
  const coApplicantIdVerificationStep = useContentSlot(
    'profile',
    'coApplicantIdVerificationStep',
    RegistrationFallback,
  );
  const coApplicantUserDetailsStep = useContentSlot('profile', 'coApplicantUserDetailsStep', RegistrationFallback);
  const coApplicantIdStepLatam = useContentSlot('profile', 'coApplicantIdStepLatam', RegistrationFallback);

  const userPreviewStep = useContentSlot('profile', 'userPreviewStep', RegistrationFallback);
  const userVerifyStepLatam = useContentSlot('profile', 'userVerifyStepLatam', RegistrationFallback);
  const inviteStep = useContentSlot('profile', 'inviteStep', RegistrationFallback);

  const componentMap: Record<string, React.ComponentType<DynamicObjectInterface>> = {
    SelectAccountTypeStep,
    UserDetailsStep,
    ContactVerificationStep: ContactVerificationStep,
    selectSponsorStep: SelectSponsorStep,
    SinglePageStep,
    idStep,
    UserDetailsStepTh,
    userDetailsStepLatam,
    coAppUserDetailsStep,
    kitStepLatam,
    // Co-app components
    // Step 1 - sending invitation (new components to add to contentslot) -->
    coApplicantIdStep,
    coApplicantInviteStep,
    coApplicantDocumentUploadStep,
    coApplicantIdStepLatam,
    userOpsCenterDetailsStep: UserOpsCenterDetailsStep,

    // Step 2 - invited/resume application
    coApplicantIdVerificationStep,
    coApplicantUserDetailsStep,
    userPreviewStep,
    userVerifyStepLatam,
    inviteStep,
  };

  // component's internal states
  const [flowSteps, setFlowSteps] = useState<StepType[]>();
  const [submitOnLastStep, setSubmitOnLastStep] = useState(false);
  const [regConfirmationData, setRegConfirmationData] = useState<IRegistrationConfirmationPropTypes>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCoAppThanksPage, setShowCoAppThanksPage] = useState<boolean>(false);
  const isMultiUserUplineFlow =
    !isAnonymous() &&
    (flowName == RegFlowConfig.AboLedFlow || (flowName === RegFlowConfig.CoApplicantFlow && idStepIsLanding));

  //local storage
  const localstorage = StorageService.getLocalStorage();
  const currentStep: StepType = flowSteps?.[currentIndex] || {};
  const totalSteps = flowSteps?.length;
  const stepName = currentStep.stepName ?? '';
  const [componentPath, setComponentPath] = useState<string>(currentStep.componentPath ?? '');

  const [showKitSelector, setShowKitSelector] = useState(false);
  const [sharecodeAttributesLocal, setSharecodeAttributesLocal] = useState<DynamicObjectInterface>({});

  // eslint-disable-next-line
  const currentFlow = useMemo<DynamicObjectInterface>(
    () => getRegistrationFlow(config, flowName, selectedAccountType, isAnonymous()),
    [config, flowName, selectedAccountType],
  );
  const registerStartedTealiumEvent = useRegistrationStartEvent();
  const registerCompleteTeliumEvent = useRegistrationCompletionEvent();

  const apiErrorHandler = useGlobalErrorHandler();
  const getComponent = (commonProps: RegistrationCommonProps) => {
    const DisplayComponentMap = componentMap[componentPath];
    return componentMap[componentPath] && <DisplayComponentMap {...commonProps} />;
  };
  const { currentCountry } = useCountryMasterData();
  const countryDialCode = `+${currentCountry?.phoneCode}`;
  const isAboLedUpline = flowName === RegFlowConfig.AboLedFlow && !isAnonymous();
  const isAboLedDownline = flowName === RegFlowConfig.AboLedFlow && isAnonymous();

  function findKeyWithValue<T>(obj: Record<string, T>, value: RegFlowConfig): string | undefined {
    return Object.keys(obj).find((key) => obj[key] === value);
  }
  const getFlowNamePayload = findKeyWithValue(RegFlowConfig, flowName) || ('' as keyof typeof RegFlowConfig);
  //RegFlowConfig needs to be changed from BE
  const createRegistrationIdPayload: RegistrationCreateRequest = {
    accountType: selectedAccountType,
    registrationFlow: RegFlowConfig[getFlowNamePayload as keyof typeof RegFlowConfig],
    returnUrl: redirectUrl,
  };

  const latestRegistrationFlowDataRef = useRef(registrationFlowData);

  useEffect(() => {
    latestRegistrationFlowDataRef.current = registrationFlowData;
  }, [registrationFlowData]);

  useEffect(() => {
    if (paramStepName) {
      updateCurrentStep(paramStepName);
    }
  }, [paramStepName]);

  useEffect(() => {
    setComponentPath(currentStep.componentPath ?? '');
  }, [currentStep]);

  const updateCurrentStep = (stepName: string) => {
    dispatch(
      updateRegistrationData({
        ...registrationFlowData,
        currentStepName: stepName,
      }),
    );
  };
  const generateRegistrationId = async (registrationData?: RegistrationFlowDataType) => {
    switch (flowName) {
      case RegFlowConfig.ShareBarFlow:
        if (sponsorNumber && sponsorName) {
          createRegistrationIdPayload.sponsor = Number(sponsorNumber);
        }
        break;
      case RegFlowConfig.AboLedFlow:
        createRegistrationIdPayload.sponsor = Number(accountNum);
        break;
      default:
        break;
    }

    try {
      setIsLoading(true);
      const res = await apiErrorHandler(() =>
        profileApi.createRegistrationId(createRegistrationIdPayload, undefined, true),
      );
      setIsLoading(false);
      const { registrationId, version, sponsorLocation, sponsor, sponsorName: sponsorUserName } = res || {};
      const regData = registrationData || registrationFlowData;
      if (
        SPONSOR_PREDEFINED_FLOW.includes(flowName) ||
        (FLOWS_WITH_POSSIBLE_SPONSOR.includes(flowName) && sponsorUserName)
      ) {
        dispatch(
          updateRegistrationData({
            ...regData,
            userData: {
              ...userData,
              registrationId: registrationId,
              patchVersion: version,
            },
            selectSponsor: {
              ...selectSponsor,
              editSponsor: isAboLedUpline || FLOWS_WITH_POSSIBLE_SPONSOR.includes(flowName),
              hideSponsorRadioSelection: true,
              sponsor: {
                accountName: sponsorUserName,
                accountNumber: sponsor,
                location: sponsorLocation,
              },
            },
          }),
        );
      } else {
        dispatch(
          updateRegistrationData({
            ...regData,
            userData: {
              ...userData,
              registrationId,
              patchVersion: version,
            },
            selectSponsor: {
              ...selectSponsor,
              editSponsor: true,
              sponsor: {},
              hideSponsorRadioSelection: false,
            },
          }),
        );
      }
    } catch (error: DynamicObjectInterface) {
      setIsLoading(false);
      if (error?.responseBody) {
        const code = error?.responseBody?.subDetails?.[0]?.code || error?.responseBody?.code;
        catchHandler(code);
      }
    }
    return null;
  };

  const catchHandler = (code: string) => {
    if (code.length === 0) {
      setModalErrorData({
        headline: ERROR_GENERIC_MESSAGE.headline,
        body: ERROR_GENERIC_MESSAGE.body,
      });
    } else {
      const invalidSponsorError =
        code === ErrorConstants.SPONSOR_FAILED || code === ErrorConstants.SPONSOR_BLOCK_VALIDATION_ERROR;
      const inValidSponsorErrorHeadline = `profile.ApiErrorModal.registration.${code}.headline`;
      const inValidSponsorErrorBody = `profile.ApiErrorModal.registration.${code}.body`;
      const headline = invalidSponsorError ? inValidSponsorErrorHeadline : `profile.ApiErrorModal.${code}.headline`;
      const body = invalidSponsorError ? inValidSponsorErrorBody : `profile.ApiErrorModal.${code}.body`;
      const buttonLabel = invalidSponsorError ? `profile.ApiErrorModal.registration.${code}.button` : undefined;
      const redirectToHomeCallback = () => {
        router.push('/');
      };

      setModalErrorData({
        headline,
        body,
        primaryButtonLabel: buttonLabel,
        primaryButtonCallback: redirectToHomeCallback,
      });
    }
  };
  const firstStepCheck = !registrationId && currentIndex === 0;
  const lastStepCheck = currentIndex + 1 === totalSteps;

  const nextHandler = async (
    payload?: NextHandlerProps,
    slicePayload?: DynamicObjectInterface,
    stayOnSameStep?: boolean,
    callOnSuccess?: boolean,
    apiCallType = APICallType.DEFAULT,
    callbackStepName = '',
    skipNextStepConfig = false,
  ) => {
    loggerService.info('Registration nextHandler', payload, slicePayload, stayOnSameStep, callOnSuccess, apiCallType);

    // Continue for existing registration flow
    const moveToNextStep = currentStep.stepConfig?.moveToNextStep;
    const sameStepCheck = stayOnSameStep || lastStepCheck || moveToNextStep === false;

    // Construct general registration data
    const nextStepNameConfig =
      selectedAccountType &&
      currentStep.config?.[selectedAccountType]?.find((configData) => configData.key === 'nextStepName')?.value;
    let nextStepId = sameStepCheck ? currentIndex : currentIndex + 1;

    if (nextStepNameConfig && !skipNextStepConfig && !stayOnSameStep) {
      const nextStepIdTemp = flowSteps?.findIndex((flowStep) => flowStep.stepName === nextStepNameConfig) ?? nextStepId;
      nextStepId = nextStepIdTemp === -1 ? nextStepId : nextStepIdTemp;
    }

    const nextStepName = flowSteps?.[nextStepId]?.stepName;

    const registrationData = {
      ...latestRegistrationFlowDataRef?.current,
      ...slicePayload,
      currentStepName: nextStepName,
      currentIndex: nextStepId,
    };

    // if step passing apiCallType as PATCH, then it will skip the final call
    const isLastStep = (lastStepCheck && apiCallType !== APICallType.PATCH) || apiCallType === APICallType.SUBMIT;
    // APICallType = SUBMIT = force to submit call

    if (firstStepCheck) {
      generateRegistrationId(registrationData);
      registerStartedTealiumEvent(selectedAccountType);
    } else if (!isLastStep) {
      // Registration Orchestrator
      return await handleCommonNextStep(registrationData, payload, apiCallType, callbackStepName);
    } else if (isLastStep) {
      const res = await handleLastStepApi(registrationData, payload, callOnSuccess, onSuccess, apiCallType);
      return res;
    }
    return {};
  };

  const handleCommonNextStep = async (
    registrationData: RegistrationFlowDataType,
    payload: NextHandlerProps,
    apiCallType?: APICallType,
    callbackStepName?: string,
  ) => {
    try {
      let res: DynamicObjectInterface;
      //can we assign this directly , why spread? do we need this object?
      let regData: DynamicObjectInterface = {
        ...registrationData,
      };

      if (apiCallType !== APICallType.SKIP) {
        const tokenHeader = generateCustomHeaders(registrationData, OtpData?.challengeId);

        res = await apiErrorHandler<RegistrationResponse>(() =>
          profileApi.patchRegistration(
            payload as RegistrationNextHandlerPatchPayload,
            registrationId,
            callbackStepName || stepName,
            tokenHeader,
          ),
        );

        const restoreDataUsingID = currentStep.stepConfig?.restoreDataUsingID;
        if (restoreDataUsingID && flowSteps) {
          await stateInitialization(currentFlow, flowSteps);
          dispatch(
            updateRegistrationData({
              currentStepName: registrationData?.currentStepName,
              currentIndex: registrationData?.currentIndex,
            }),
          );
          return { ...res, currentIndex: registrationData?.currentIndex };
        }

        if (registrationFlowData?.aboKitVerified) {
          setSubmitOnLastStep(true);
        }
        // Update personal details to redux
        const updatedPersonalDetails = payload?.personalId?.documentData
          ? getPersonalDetailData(res, res?.personalId?.recordType)
          : null;

        const updatedDocumentData = payload?.personalIdList?.[0]?.documentDataList ?? [];
        const documentDataListFiltered = documentDataList?.filter(
          (data) => data?.type !== updatedDocumentData?.[0]?.type,
        );

        // CoApp ID Verification Data
        let coAppVerificationData = {};
        let coAppPersonalData = {};
        if (res.status === RegistrationStatus.CoapplicantInvited) {
          // To append partner ID & Type
          coAppVerificationData = {
            spousePersonalId: res?.personalId?.recordNumber,
            spouseRecordType: res?.personalId?.recordType,
          };
          coAppPersonalData = {
            citizenshipCountry: res?.citizenshipCountry,
          };
        }

        // reassign here so no need obj copy above
        regData = {
          ...registrationData,
          cidVerification: {
            ...cidVerification,
            ...registrationData.cidVerification,
            recordType: registrationData?.cidVerification?.verifyUser
              ? res?.personalId?.recordType
              : registrationData?.cidVerification?.recordType,
            ...coAppVerificationData,
          },
          userData: {
            ...registrationData.userData,
            accountNum: res?.accountNumber,
            patchVersion: res.version,
          },
          perosonalDetails: {
            ...registrationData?.perosonalDetails,
            ...perosonalDetails,
            ...updatedPersonalDetails,
            ...coAppPersonalData,
          },
          documentDataList: [...documentDataListFiltered, ...updatedDocumentData],
        };
      }
      dispatch(
        updateRegistrationData({
          ...registrationData,
          ...regData,
        }),
      );

      return { ...res, currentIndex: registrationData?.currentIndex };
    } catch (error: DynamicObjectInterface) {
      //patch error return to the component
      return error;
    }
  };

  const lastStepSubmitHandler = async (
    payload: RegistrationSubmitRequest,
    registrationData: RegistrationFlowDataType,
    tokenHeader?: DynamicObjectInterface,
  ) => {
    try {
      setIsLoading(true);
      const res = await apiErrorHandler<RegistrationSubmitResponse>(() =>
        profileApi.postSubmitRegistration(payload as RegistrationSubmitRequest, registrationId, tokenHeader),
      );
      setIsLoading(false);
      registerCompleteTeliumEvent(selectedAccountType);
      if (flowName === RegFlowConfig.OpsLedFlow) {
        if (onSuccess) {
          onSuccess({ ...payload, ...res } as onSuccessPropType);
        }
        generateRegistrationId();
        return undefined;
      }

      if (flowName === RegFlowConfig.CoApplicantFlow) {
        dispatch(
          updateRegistrationData({
            ...registrationData,
            userData: {
              ...registrationData.userData,
              accountNumber: res.accountNumber,
              coAppRegisterSuccess: true,
            },
          }),
        );
        return undefined;
      }
      if (submitOnLastStep || registrationFlowData?.aboKitVerified) {
        setRegistrationConfirmationData(res);
      }
      loginAndRedirectAfterRegistration(res?.bcAuthToken, redirectUrl, apiErrorHandler);
    } catch (error: DynamicObjectInterface) {
      loggerService.log(error);
      setIsLoading(false);
      if (error?.responseBody) {
        const { code } = error.responseBody;
        if (registrationFlowData?.aboKitVerified) {
          setRegConfirmationData({
            errorCode: code,
          });
        } else if (flowName === RegFlowConfig.OpsLedFlow) {
          return error;
        } else {
          catchHandler(code);
        }
      }
    }
    return undefined;
  };

  async function handleLastStepApi(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registrationData: any,
    payload: NextHandlerProps,
    callOnSuccess: boolean | undefined,
    onSuccess: DynamicObjectInterface,
    apiCallType = APICallType.DEFAULT, // APICallType = SUBMIT = force to submit call
  ) {
    const tokenHeader = generateCustomHeaders(registrationData, OtpData?.challengeId);
    deleteRegistrationInfoFromSession();
    if ((submitOnLastStep && !isMultiUserUplineFlow) || apiCallType === APICallType.SUBMIT) {
      // Move to function to reduce cognitive complexity
      const submitResp = await lastStepSubmitHandler(
        payload as RegistrationSubmitRequest,
        registrationData,
        tokenHeader,
      );
      return submitResp;
    } else {
      // This function will return default redirectUrl to returnUrl if not skip cart flow
      // If it's skip cart flow, it will return to checkout
      const { starterKitSku, feeSku, useNewCartForCheckout, returnUrl, rejected } = await checkForSkipCart(
        flowName,
        redirectUrl,
        setShowKitSelector,
        sharecodeAttributesLocal?.category,
      );
      if (rejected) {
        return undefined;
      }
      try {
        setIsLoading(true);
        const res = await apiErrorHandler<RegistrationResponse>(() =>
          profileApi.patchRegistration(
            payload as RegistrationNextHandlerPatchPayload,
            registrationId,
            stepName,
            tokenHeader,
          ),
        );
        const updatedPersonalDetails = getPersonalDetailData(res, cidVerification?.recordType);
        const autoAssignSponsor = updateSponsor(res);
        dispatch(
          updateRegistrationData({
            ...registrationData,
            userData: {
              ...userData,
              accountNum: res?.accountNumber,
              patchVersion: res.version,
            },
            perosonalDetails: {
              ...perosonalDetails,
              ...updatedPersonalDetails,
            },
          }),
        );
        const registrationAttributes = {
          accountType: selectedAccountType,
          sponsor: updateSponsorAttribute(autoAssignSponsor, '', sponsor),
        };
        if (isMultiUserUplineFlow) {
          // user is loggedin, then its upline
          setIsLoading(false);
          if (flowName === RegFlowConfig.AboLedFlow) {
            setRegistrationConfirmationData(res);
          } else {
            setShowCoAppThanksPage(true);
          }
        }

        localstorage.store(ProfileConstantsEnum.registrationAttributes, registrationAttributes);
        if (!callOnSuccess) {
          setIsLoading(false);
        }
        if (callOnSuccess) {
          onSuccess({
            registrationId,
            accountType: selectedAccountType,
            kitSku: starterKitSku,
            feeSku: feeSku,
            useNewCartForCheckout,
            returnUrl, // re-write the final URL if it's skip cart flow
          });
        }

        /* Skip Cart Info -->
        if kitSku and useNewCartForCheckout --> create new cart and checkout without redirect to cart
        if kitSku and !useNewCartForCheckout --> append sku into existing cart. Then redirect to checkout
        <-- */
        return res;
      } catch (error: DynamicObjectInterface) {
        // component would handle this patch error
        setIsLoading(false);
        return error;
      }
    }
  }

  const setRegistrationConfirmationData = (regData: RegistrationResponse | ErrorResponseType) => {
    const prospectData: IProspectInfo = {};
    if ('registrationId' in regData) {
      if (isAboLedUpline) {
        prospectData.email = regData?.email;
        prospectData.firstName = regData?.familyName;
        prospectData.lastName = regData?.givenName;
        prospectData.phonenumber = regData?.phone;
        prospectData.primaryContactType = regData?.usernameContactType;
      }
      setRegConfirmationData({
        accountId: isAboLedDownline ? regData?.registeredAccountNumber : regData?.accountNumber,
        accountType: selectedAccountType,
        sponsorAccountId: regData?.sponsor,
        sponsorName: regData?.sponsorName,
        flowName: registrationFlowData?.flowName,
        email: regData?.email,
        phonenumber: regData?.phone,
        primaryContactType: regData?.usernameContactType,
        prospectInfo: prospectData,
      });
    }
  };

  const commonProps: RegistrationCommonProps = {
    nextHandler: nextHandler,
    setSubmitHandler,
  };

  useEffect(() => {
    if (currentStep && currentStep.stepName) {
      const isNotAccountSelectionStep = currentStep.stepName != 'selectAccountTypeStep';
      const isShopShareBarFlow = sharecodeAttributesLocal?.category === SHAREBAR_FLOWS.MYSHOP;
      const defaultAccountTypeForShopFlow = currentStep?.stepConfig?.defaultAccountTypeForShopFlow;
      if (updateStepToNextAllowedForAccountType(currentIndex, selectedAccountType)) {
        return; //step was updated due to show : false for account type.
      }
      if (
        isNotAccountSelectionStep &&
        !registrationId &&
        !paramRegId &&
        selectedAccountType &&
        (accountType || isShopShareBarFlow)
      ) {
        generateRegistrationId();
        registerStartedTealiumEvent(accountType || selectedAccountType);
      }
      if (!isNotAccountSelectionStep && accountType) {
        const nextStepName = flowSteps?.[currentIndex + 1]?.stepName;
        dispatch(
          updateRegistrationData({
            currentStepName: nextStepName,
            currentIndex: currentIndex + 1,
          }),
        );
      }
      if (!isNotAccountSelectionStep && isShopShareBarFlow && !accountType && defaultAccountTypeForShopFlow) {
        dispatch(updateCustomerType(defaultAccountTypeForShopFlow));
        const nextStepName = flowSteps?.[currentIndex + 1]?.stepName;
        dispatch(
          updateRegistrationData({
            currentStepName: nextStepName,
            currentIndex: currentIndex + 1,
          }),
        );
      }
    }
  }, [currentStep]);

  useEffect(() => {
    // Read config from master data
    const localstorage = StorageService.getLocalStorage();
    const sharecodeAttr: DynamicObjectInterface = localstorage.retrieve('sharecodeAttributes');
    setSharecodeAttributesLocal(sharecodeAttr);
    sendUtagView(flowName, dataLayer);
    try {
      if (currentFlow) {
        // Transform Step Data into Array
        if (currentFlow.steps) {
          const stepsArray: DynamicObjectInterface = Object.entries<StepType>(currentFlow.steps).map((stepConfig) => ({
            stepName: stepConfig[0],
            componentPath: stepConfig[1]?.componentPath,
            stepConfig: stepConfig[1]?.stepConfig,
            canSkip: stepConfig[1]?.canSkip,
            config: stepConfig[1]?.config,
          }));
          dispatch(updateCurrentFlowStepsData(stepsArray));
          setFlowSteps(stepsArray);
          stateInitialization(currentFlow, stepsArray, paramStepName);

          if (accountType) {
            dispatch(updateCustomerType(accountType));
          }
        }
      } else {
        throw Error(t('profile.Registration.flowName.notFound.Error'));
      }
    } catch (error) {
      loggerService.error(error);
    }
    // set profile data on initial load
    if (profileInfoCms) {
      const profiletransformedData = getProfileImagesCmsDataTransformer(profileInfoCms);
      dispatch?.(updateRegistrationCmsData(profiletransformedData?.data));
    }

    // Clean up previous errors
    dispatch(updateApiErrorData({ message: '' }));

    return () => {
      dispatch(resetRegistrationState());
    };
  }, []);
  useEffect(() => {
    if (currentFlow?.orderRequired && selectedAccountType) {
      const submitOnLastStep = currentFlow?.orderRequired?.[selectedAccountType] === false ? true : false;
      setSubmitOnLastStep(submitOnLastStep);
    } else {
      setSubmitOnLastStep(currentFlow?.submitOnLastStep);
    }
  }, [currentFlow, selectedAccountType]);

  useEffect(() => {
    localstorage.store(ProfileConstantsEnum.registrationAccountType, selectedAccountType);
    if (selectedAccountType) {
      // Registration images & t&c - add locale to fetch the correct data when changing locale
      const selectedAccountCmsData = accountTypeImagesCms?.find(
        (data: AccountTypeImagesResult) =>
          data?.user_account?.account_type?.toLowerCase() === selectedAccountType.toLowerCase(),
      );
      const accountResults = getAccountTypeImagesCmsDataTransformer(selectedAccountCmsData as AccountTypeImagesResult);
      accountResults.data.locale = siteLocale;

      dispatch(
        updateAccountTypeImagesCMS({
          type: selectedAccountType,
          data: accountResults.data,
        }),
      );

      const selectedAccTCCmsData = termsAndConditionsCms?.find(
        (data: TermsConditionResult) =>
          data?.account_type?.account_type?.toLowerCase() === selectedAccountType.toLowerCase(),
      );

      if (registrationOrderConfirmImagesCMS?.length) {
        dispatch(updateRegistrationOrderConfirmationImagesCMSData(registrationOrderConfirmImagesCMS));
      }

      const tcResults = getTermsConditionCmsDataTransformer(selectedAccTCCmsData as TermsConditionResult);
      tcResults.data.locale = siteLocale;

      dispatch(
        updateTermsNConditionsCMS({
          type: selectedAccountType,
          data: tcResults.data,
        }),
      );
    }
  }, [selectedAccountType]);

  const stateInitialization = async (currentFlow: DynamicObjectInterface, steps: StepType[], paramStep = '') => {
    let getUserType: string[];
    let currentStep = '' as string,
      initialStepId = 0,
      registrationId = '';

    let selectAccountType: DynamicObjectInterface = null;

    if (paramStep !== '') {
      currentStep = paramStep;
      initialStepId = steps?.findIndex((step: StepType) => step.stepName === currentStep);
    } else {
      currentStep = stepName;
    }
    if (paramRegId !== '') {
      registrationId = paramRegId;
    }

    // Transform userType Data into Array from stepConfig
    if ('selectAccountTypeStep' in currentFlow.steps) {
      getUserType = Object.keys(currentFlow.steps['selectAccountTypeStep'].stepConfig.userType);
      selectAccountType = {
        accountTypes: getUserType.sort(),
      };
      if (!accountType) {
        dispatch(updateCustomerType(getUserType[0]));
      }
    }

    const initCallback = (registerInitData: InitialProfileReduxUpdate) => {
      dispatch(
        updateRegistrationData({
          ...registrationFlowData,
          flowName: flowName,
          findOutMoreUrl: findOutMoreUrl,
          redirectUrl: redirectUrl,
          currentIndex: initialStepId,
          currentStepName: currentStep,
          isSelfInitiatedFlowV2: flowName === RegFlowConfig.SelfInitiatedFlowV2,
          selectAccountType,
          ...registerInitData?.mainData,
          userData: {
            ...userData,
            registrationId: registrationId,
            ...registerInitData?.userData,
          },
          perosonalDetails: {
            ...perosonalDetails,
            ...registerInitData?.perosonalDetails,
          },
          coAppPersonalDetails: {
            ...coAppPersonalDetails,
            ...registerInitData?.coAppPersonalDetails,
          },
          selectSponsor: {
            ...selectSponsor,
            ...registerInitData?.selectSponsor,
            sponsor: {
              ...selectSponsor?.sponsor,
              ...registerInitData?.selectSponsor?.sponsor,
            },
          },
          OtpData: {
            ...registrationFlowData.OtpData,
            ...registerInitData?.OtpData,
          },
        }),
      );
      if (registerInitData?.accountType) {
        dispatch(updateCustomerType(registerInitData?.accountType));
      }
    };

    await restoreRegistrationDataFromSession(steps, initCallback, setSubmitOnLastStep, countryDialCode);
  };

  function isStepVisibleForAccountType(currentStepIndex: number, selectedAccountType: AccountType): boolean {
    //canSkip is not being used on FE only on BE.
    const showNextComponent = flowSteps?.[currentStepIndex]?.config?.[selectedAccountType]?.find(
      (item) => item.key === 'show',
    );
    if (showNextComponent?.value == 'false') {
      return false;
    }
    return true;
  }
  function updateStepToNextAllowedForAccountType(currentIndex: number, selectedAccountType: AccountType): boolean {
    if (flowSteps?.length) {
      if (!isStepVisibleForAccountType(currentIndex, selectedAccountType)) {
        const updatedIndex = currentIndex + 1;
        if (flowSteps[updatedIndex]?.stepName) {
          const curStep = flowSteps[updatedIndex];
          const registrationData = {
            ...registrationFlowData,
            currentStepName: curStep.stepName,
            currentIndex: updatedIndex,
          };
          dispatch(updateRegistrationData(registrationData));
          return true;
        }
      }
    }
    return false;
  }

  return (
    <>
      {isLoading && (
        <div className={styles['registration-loading-container']} data-testid="registration-loading-container">
          <PageLoader />
        </div>
      )}
      <>
        {!isDuplicateUser && !regConfirmationData && !showCoAppThanksPage && (
          <div
            className={`${styles['registration-container']}
            ${isLoading ? styles['display-none'] : ''}`}
            data-testid="registration-container"
          >
            <div className={styles['registration-body']} data-testid="registration-body">
              {getComponent(commonProps)}
            </div>
          </div>
        )}
        {(submitOnLastStep || registrationFlowData?.aboKitVerified || flowName === RegFlowConfig.AboLedFlow) &&
          regConfirmationData && <RegistrationConfirmation {...regConfirmationData} />}
        <RegistrationSelectStarterKit
          accountType={selectedAccountType}
          onConfirm={(window as DynamicObjectInterface).handleSkuSelect}
          openSKUSelector={showKitSelector}
        />
        {showCoAppThanksPage && (
          <CoAppCommonScreen
            buttonHandler={() => router.push(redirectUrl)}
            screenType={CoAppScreenEnum.INVITATION_SENT}
          />
        )}
        {<ApiErrorModal {...modalErrorData} />}
        {isDuplicateUser && <RegistrationSignInModal defaultLoginIdentityProps={getLoginRedirectProps(sourceUrl)} />}
      </>
    </>
  );
};

export default Registration;
